import { defineMessages } from 'react-intl'

import { createSelector } from '@app/utils/createSelector'
import { captureException } from '@app/utils/errorReport/errorReport'

import type {
  ApiAccountIssue,
  ApiAccountIssueStep,
  ApiAccountIssueStepMeetingRegistration,
  ApiAccountIssueStepMeetingViolations,
  ApiAccountIssueStepOnlineRecoveryMeeting,
  ApiAccountIssueStepReactivationInterview,
  ApiAccountIssueStepRulesTest,
  ApiAccountIssueStepSupportDayVisit,
  ApiOnboardingStepTestingTestLinkData,
} from '@app/packages/api/v2/types/entities'
import { getRelationshipValue } from '@app/packages/apiTools/getRelationshipValue'
import { createLoadable, LoadRequest } from '@app/packages/thunkSelector/thunkSelector'

import { profileUserResultSelector } from '@app/store/selectors/profile'

import { accountIssuesListSelector } from './account_issues'
import { intlSelector } from './intl'
import { profileUserSelector } from './profile'

export type SuspendModeAccesses = {
  chat?: boolean
  supportChat?: boolean
  profile?: boolean
  settings?: boolean
  announcements?: boolean
}

export type ResolutionStepMeetingKind = 'support_day_visit' | 'online_recovery_meeting' | 'reactivation_interview'

export type ResolutionStepMeeting = {
  [K in ResolutionStepMeetingKind]: {
    type: K
    resolved: boolean
    cascaded_from?: ResolutionStepMeetingKind | null
    meeting_violations?: ApiAccountIssueStepMeetingViolations
    registration?: ApiAccountIssueStepMeetingRegistration | null
  }
}[ResolutionStepMeetingKind]

export type ResolutionStep =
  | {
      type: 'rules_test'
      url?: string
      test_link_data?: ApiOnboardingStepTestingTestLinkData
      attempts_count?: number
      max_attempts_count?: number | null
      resolved: boolean
      failed: boolean
    }
  | ResolutionStepMeeting
  | { type: 'unknown'; label: string; resolved: boolean }

export type SuspendMode =
  | { type: 'permanent' }
  | { type: 'unknown'; reason?: string }
  | { type: 'unpaid bill'; bill_id: string }
  | { type: 'profile_issues' }
  | { type: 'passport_upload_required' }
  | { type: 'inactivity'; steps: ResolutionStep[] }
  | { type: 'rules_violation'; steps: ResolutionStep[] }

export const profileSuspendModeResultSelector = createSelector(
  [profileUserResultSelector, accountIssuesListSelector, intlSelector],
  (profileResult, issuesResult, intl) =>
    createLoadable((c): SuspendMode | null => {
      const profile = c.unwrap(profileResult)
      if (!profile || profile.account_type === 'visitor') return null
      if (profile.account_type === 'sitter' && profile.banned_permanently) {
        return { type: 'permanent' }
      }
      const issues = c.unwrap(issuesResult)
      const activeIssue = issues.data.find(i => i.attributes.status === 'pending')
      if (!activeIssue) {
        if (issues.data.some(i => i.attributes.status === 'failed')) return { type: 'unknown' }
        return null
      }
      switch (activeIssue.attributes.type) {
        case 'unpaid_training_payment_contract_bill': {
          if (!activeIssue.relationships?.bill) {
            captureException(new Error('Unpaid bill issue is missing bill relationship'), { extra: { issueId: activeIssue.id } })
            return { type: 'unknown', reason: activeIssue.attributes.reason ?? intl.formatMessage(messages.unpaid_bill) }
          }
          return {
            type: 'unpaid bill',
            bill_id: getRelationshipValue(activeIssue.relationships.bill).id,
          }
        }
        case 'passport_upload_required':
          return { type: 'passport_upload_required' }
        case 'profile_issues':
          return { type: 'profile_issues' }
        case 'rules_violation': {
          const steps = parseResolutionSteps(activeIssue, issues.included)
          if (!steps) return { type: 'unknown', reason: activeIssue.attributes.reason ?? intl.formatMessage(messages.rules_violation) }
          return { type: 'rules_violation', steps }
        }
        case 'inactivity': {
          const steps = parseResolutionSteps(activeIssue, issues.included)
          if (!steps) return { type: 'unknown', reason: activeIssue.attributes.reason ?? intl.formatMessage(messages.inactivity) }
          return { type: 'inactivity', steps }
        }
        default:
          return { type: 'unknown', reason: activeIssue.attributes.reason ?? undefined }
      }
    })
)

/**
 * Suspended as the suspension page decides it, plus a sitter's own blocked flags; `null` while the
 * account issues load, so a reload in flight reads as no change.
 */
export const profileSuspendedSelector = createSelector([profileUserSelector, profileSuspendModeResultSelector], (user, modeResult) => {
  if (user?.account_type === 'sitter' && (user.banned_permanently || user.blocked)) return true
  if (modeResult.error) return null
  const mode = modeResult.value.value
  return mode instanceof LoadRequest ? null : mode !== null
})

const parseResolutionSteps = (issue: ApiAccountIssue, included: ApiAccountIssueStep[]): ResolutionStep[] | null => {
  if (!issue.relationships?.account_issue_steps?.data) return null
  const steps: ResolutionStep[] = []
  for (const s of issue.relationships.account_issue_steps.data) {
    const step = included.find(i => i.id === s.id)
    if (!step) throw new Error(`Account issue step with id ${s.id} not found in included data`)
    switch (step.attributes.step_type) {
      case 'rules_test':
      case 'recovery_test': {
        const st = step as ApiAccountIssueStepRulesTest
        const data = st.attributes.data
        steps.push({
          type: 'rules_test' as const,
          url: data?.test_link,
          test_link_data: data?.test_link_data,
          attempts_count: data?.attempts_count,
          max_attempts_count: data?.max_attempts_count,
          resolved: st.attributes.status === 'completed',
          failed: st.attributes.status === 'failed',
        })
        break
      }
      case 'support_day_visit':
      case 'online_recovery_meeting':
      case 'reactivation_interview': {
        const st = step as ApiAccountIssueStepSupportDayVisit | ApiAccountIssueStepOnlineRecoveryMeeting | ApiAccountIssueStepReactivationInterview
        steps.push({
          type: step.attributes.step_type,
          resolved: step.attributes.status === 'completed',
          cascaded_from: st.attributes.data?.cascaded_from ?? null,
          meeting_violations: st.attributes.data?.meeting_violations,
          registration: st.attributes.data?.registration ?? null,
        })
        break
      }
      default: {
        steps.push({ type: 'unknown' as const, label: step.attributes.step_type, resolved: step.attributes.status === 'completed' })
      }
    }
  }
  if (!steps.length) return null
  return steps
}

export const profileSuspendAccessesSelector = createSelector([profileSuspendModeResultSelector], suspendModeResult =>
  createLoadable((c): SuspendModeAccesses => {
    const mode = c.unwrap(suspendModeResult)
    if (!mode) {
      return {}
    }
    switch (mode.type) {
      case 'permanent':
        return {
          chat: false,
          supportChat: false,
          announcements: false,
        }
      case 'profile_issues':
      case 'passport_upload_required':
      case 'unpaid bill':
      case 'inactivity':
      case 'rules_violation':
      case 'unknown':
        return {
          chat: false,
          announcements: false,
        }
    }
  })
)

const messages = defineMessages({
  inactivity: { id: "store.slices.profile_suspend.messages.inactivity.631f", defaultMessage: 'Неактивность аккаунта' },
  rules_violation: { id: "store.slices.profile_suspend.messages.rules_violation.48e3", defaultMessage: 'Нарушение правил' },
  unpaid_bill: { id: "store.slices.profile_suspend.messages.unpaid_bill.fa84", defaultMessage: 'Неоплаченный счет' },
})
